/**
 * Mixin for components that need to sync their state with URL query params. When implemented, relevant
 * state changes are reflected in the URL query params, and vice versa. This allows for component state
 * to be persisted across page reloads, and for it to be shared via URL links.
 *
 * Only non-default option values are encoded in the URL, to keep the URL clean and readable.
 * Non-relevant query params are ignored but preserved in the URL. Syncing only occurs in admin app
 * mode, as query param changes cause undefined behavior in other app modes.
 *
 * The component must receive the `enableQueryEncodableOptions` prop. Any wrapping Table components for
 * paginatable models should set the model `immediate: false` option to let ModelTable handle the initial
 * query with decoded options. The component must also implement the following.
 * - `defaultQueryEncodableOptions()` computed property
 * - `decodeUrlParamOption(key, value)` method
 */
export default {
  props: {
    enableQueryEncodableOptions: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      hasLoadedWithInitialQueryParams: false
    }
  },
  // We need to ensure that updates to the URL query params happen only after the initial component has fetched data
  // using the initially decoded URL query params. If we don't wait, it is possible for the watchers to accidentally
  // immediately replace any existing URL query params with the starting default option values before the initial fetch.
  async mounted () {
    await this.$nextTick()
    this.hasLoadedWithInitialQueryParams = true
  },
  computed: {
    /**
     * This toggles this feature on or off. This feature is intended for the Admin ModelTables that serve as
     * model index pages, navigable by URLs. Nested tables must not enable this feature because their options
     * will likely conflict with the parent table's options.
     *
     * NOTE: There is a possible improvement here, where we can add a prefix to all query param names
     * to avoid conflicts between nested tables. This would allow multiple tables present on the same page to
     * encode and decode their options without conflict.
     */
    shouldEncodeAndDecodeQueryParams () {
      // Model tables are also used in non-admin app modes, but these query params are not designed for them
      if (!this.$admin) return false
      // This feature is not designed for nested model tables
      if (this.submodel) return false

      // The base ModelTable component is a special case because it is usually immediately wrapped in a SpecificModelTable
      // so it won't get the enableQueryEncodableOptions prop passed to it. We need to check the prop on the wrapper component.
      if (this.$options.name === 'ModelTable' && !!this.$parent) {
        if (this.$parent.shouldEncodeAndDecodeQueryParams !== undefined) {
          return this.$parent.shouldEncodeAndDecodeQueryParams
        } else if (this.$parent.$attrs['enable-query-encodable-options'] !== undefined) {
          // This is the case where the wrapper Table component has not included this mixin but
          // we still want to enable its base ModelTable component to encode pagination and filtering.
          return true
        }
      }

      return this.enableQueryEncodableOptions
    },
    /**
     * Returns an object containing all possible option key-values that can be encoded in the URL.
     * The values should be the default values for the options, usually the initial data() or prop
     * values, or the computed values expected at the component's initial state. This should always
     * return the same key-values.
     * @returns {Record<string, any>}
     */
    defaultQueryEncodableOptions () {
      throw new Error('`defaultQueryEncodableOptions` computed property must be implemented by the component')
      // Example implementation:
      // const defaultOptions = {
      //   // from props
      //   userId: this.userId,
      //   // from initial data
      //   userRole: 'users'
      // }
      // // from computed
      // const dateRange = this.getRelativeDateRange(this.getDefaultLookbackDays())
      // if (_.isObject(dateRange)) {
      //   defaultOptions.rangeStart = dateRange.from
      //   defaultOptions.rangeEnd = dateRange.to
      // }
      // return defaultOptions
    },
    /**
     * Returns an object containing valid options decoded from the current URL query params.
     * The object should contain only options that return a valid value from the decodeUrlParamOption() method
     * and that do not match the default values for the options. We omit default values to avoid redundant
     * query params in the URL.
     * @returns {Record<string, any>}
     */
    currentQueryDecodedOptions () {
      const decodedOptions = {}
      _.each(this.$route.query, (value, key) => {
        if (_.isNumber(value) || _.isBoolean(value) || !_.isEmpty(value)) {
          const decodedValue = this.decodeUrlParamOption(key, value)
          if (decodedValue !== undefined && decodedValue !== this.defaultQueryEncodableOptions[key]) {
            decodedOptions[key] = decodedValue
          }
        }
      })
      return decodedOptions
    },
    /**
     * Returns an object containing current options that can be encoded in the URL, excluding options
     * whose values match the default values.  We omit default values to avoid redundant query params
     * in the URL.
     *
     * It is common to override this method in the component to handle values that require special
     * computation.
     * @returns {Record<string, any>}
     */
    currentQueryEncodableOptions () {
      const optionsToEncode = {}
      _.each(_.keys(this.defaultQueryEncodableOptions), (key) => {
        const currentValue = this[key]
        if (_.isNumber(currentValue) || _.isBoolean(currentValue) || !_.isEmpty(currentValue)) {
          if (currentValue !== this.defaultQueryEncodableOptions[key]) {
            optionsToEncode[key] = currentValue
          }
        }
      })
      return optionsToEncode
    }
  },
  // By default, mixin watchers cannot be overwritten by the component; if a component defines a watcher with
  // the same key the mixin watcher will be called first, followed by the component watcher. We have at least
  // one case where we actually want to replace the mixin watcher with a component watcher, so we use custom
  // properties here.
  replaceCurrentQueryDecodedOptionsWatcher: false,
  replaceCurrentQueryEncodableOptionsWatcher: false,
  watch: {
    // this `immediate: true` watcher happens to fire before models' `variables()` functions are executed,
    // so we can rely on this to read options from query params before triggering model (re-)fetches.
    currentQueryDecodedOptions: {
      handler () {
        if (this.$options.replaceCurrentQueryDecodedOptionsWatcher) return false
        if (!this.shouldEncodeAndDecodeQueryParams) return

        if (!_.isEqual(this.currentQueryDecodedOptions, this.currentQueryEncodableOptions)) {
          this.handleQueryParamsChange()
        }
      },
      immediate: true
    },
    currentQueryEncodableOptions () {
      if (this.$options.replaceCurrentQueryEncodableOptionsWatcher) return false
      if (!this.shouldEncodeAndDecodeQueryParams) return

      if (!_.isEqual(this.currentQueryEncodableOptions, this.currentQueryDecodedOptions)) {
        this.handleComponentOptionsChange()
      }
    }
  },
  methods: {
    /**
     * Initializes the component's values, usually data() values, from the current decoded URL query params, if any.
     * This method should be safe to call multiple times. This method fires once when the component is created,
     * setting the default values, and then fires again each time query params change, setting any decoded param values.
     *
     * It is common to override this method in the component to handle values that require special computation.
     */
    initializeQueryEncodableOptions () {
      _.each(this.defaultQueryEncodableOptions, (defaultValue, key) => {
        const maybeDecodedValue = this.currentQueryDecodedOptions[key]
        if (_.isBoolean(maybeDecodedValue) || _.isNumber(maybeDecodedValue) || !!maybeDecodedValue) {
          this[key] = maybeDecodedValue
        } else {
          this[key] = defaultValue
        }
      })
    },
    /**
     * Method that decodes a single URL query param value into a valid option value. This method
     * should return `undefined` for any invalid keys or values. For example, this method should
     * handle parsing values into the correct data type, and ensuring that the values make sense.
     * @param {string} key The query param key
     * @param {string} value The query param value, expected to be a string
     * @returns {any} The decoded option value, or `undefined` if the value is invalid and should be ignored
     */
    decodeUrlParamOption (key, value) {
      throw new Error('decodeUrlParamOption() method must be implemented by the component')
      // Example implementation:
      // switch (key) {
      //   case 'userRole':
      //     return _.includes(_.map(this.filters, 'value'), value) ? value : undefined
      //   case 'userId':
      //   case 'showingId':
      //     return parseInt(value) || undefined
      //   case 'state':
      //     return value || undefined
      //   case 'rangeStart':
      //   case 'rangeEnd':
      //     return IsoDate.parse(value).isDate() ? IsoDate.parse(value).format('YYYY-MM-DD') : undefined
      //   case 'showAllDates':
      //     return String(value) === 'true'
      //   default:
      //     return undefined
      // }
    },
    /**
     * Responds to changes in the URL query params. The default action is to re-initialize the component's
     * state reading the new query param values.
     */
    handleQueryParamsChange () {
      this.initializeQueryEncodableOptions()
    },
    /**
     * Responds to changes in the component's state. The default action is to update the URL query params
     * with the new option values.
     */
    handleComponentOptionsChange () {
      if (!this.hasLoadedWithInitialQueryParams) return

      const nonOptionParams = _.omit(this.$route.query, _.keys(this.defaultQueryEncodableOptions))
      const newSortedQuery = _({ ...nonOptionParams, ...this.currentQueryEncodableOptions }).toPairs().sortBy(0).fromPairs().value()
      this.$router.push({ path: this.$route.path, query: newSortedQuery })
    }
  }
}
